import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

// Get all reviews for a product
export const getProductReviews = async (productId) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  try {
    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('productId', '==', productId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(reviewsQuery);
    
    const reviews = [];
    
    for (const docSnapshot of querySnapshot.docs) {
      const review = {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
      
      // Get user data for the review
      const userDoc = await getDoc(doc(db, 'users', review.userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        review.userName = userData.name;
        review.userAvatar = userData.avatar || null;
      }
      
      reviews.push(review);
    }
    
    return reviews;
  } catch (error) {
    throw error;
  }
};

// Get average rating for a product
export const getProductRating = async (productId) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  try {
    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('productId', '==', productId)
    );
    
    const querySnapshot = await getDocs(reviewsQuery);
    
    let totalRating = 0;
    let count = 0;
    
    querySnapshot.forEach((doc) => {
      const review = doc.data();
      totalRating += review.rating;
      count++;
    });
    
    const average = count > 0 ? (totalRating / count).toFixed(1) : 0;
    
    return {
      average: parseFloat(average),
      count
    };
  } catch (error) {
    throw error;
  }
};

// Check if a user has already reviewed a product
export const hasUserReviewed = async (productId, userId) => {
  if (!productId || !userId) {
    throw new Error('Product ID and User ID are required');
  }

  try {
    const reviewQuery = query(
      collection(db, 'reviews'),
      where('productId', '==', productId),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(reviewQuery);
    
    return !querySnapshot.empty;
  } catch (error) {
    throw error;
  }
};

// Add a new review
export const addReview = async (productId, userId, reviewData) => {
  try {
    // Check if user has already reviewed this product
    const hasReviewed = await hasUserReviewed(productId, userId);
    
    if (hasReviewed) {
      throw new Error('You have already reviewed this product');
    }
    
    // Add review to Firestore
    const reviewRef = await addDoc(collection(db, 'reviews'), {
      productId,
      userId,
      rating: reviewData.rating,
      title: reviewData.title,
      comment: reviewData.comment,
      helpful: 0,
      helpfulUsers: [],
      reported: false,
      reportCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update product rating in products collection
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (productDoc.exists()) {
      const product = productDoc.data();
      const currentRating = product.rating || { count: 0, total: 0, average: 0 };
      
      const newCount = currentRating.count + 1;
      const newTotal = currentRating.total + reviewData.rating;
      const newAverage = (newTotal / newCount).toFixed(1);
      
      await updateDoc(productRef, {
        rating: {
          count: newCount,
          total: newTotal,
          average: parseFloat(newAverage)
        }
      });
    }
    
    // Get the complete review data
    const reviewDoc = await getDoc(reviewRef);
    
    return {
      id: reviewRef.id,
      ...reviewDoc.data()
    };
  } catch (error) {
    throw error;
  }
};

// Edit a review
export const editReview = async (reviewId, updatedData) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    const reviewDoc = await getDoc(reviewRef);
    
    if (!reviewDoc.exists()) {
      throw new Error('Review not found');
    }
    
    const review = reviewDoc.data();
    const oldRating = review.rating;
    const newRating = updatedData.rating;
    
    // Update review
    await updateDoc(reviewRef, {
      ...updatedData,
      updatedAt: serverTimestamp()
    });
    
    // If rating changed, update product rating
    if (oldRating !== newRating) {
      const productRef = doc(db, 'products', review.productId);
      const productDoc = await getDoc(productRef);
      
      if (productDoc.exists()) {
        const product = productDoc.data();
        const currentRating = product.rating || { count: 0, total: 0, average: 0 };
        
        const newTotal = currentRating.total - oldRating + newRating;
        const newAverage = (newTotal / currentRating.count).toFixed(1);
        
        await updateDoc(productRef, {
          rating: {
            count: currentRating.count,
            total: newTotal,
            average: parseFloat(newAverage)
          }
        });
      }
    }
    
    // Get updated review
    const updatedReviewDoc = await getDoc(reviewRef);
    
    return {
      id: reviewId,
      ...updatedReviewDoc.data()
    };
  } catch (error) {
    throw error;
  }
};

// Delete a review
export const deleteReview = async (reviewId) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    const reviewDoc = await getDoc(reviewRef);
    
    if (!reviewDoc.exists()) {
      throw new Error('Review not found');
    }
    
    const review = reviewDoc.data();
    
    // Delete review
    await deleteDoc(reviewRef);
    
    // Update product rating
    const productRef = doc(db, 'products', review.productId);
    const productDoc = await getDoc(productRef);
    
    if (productDoc.exists()) {
      const product = productDoc.data();
      const currentRating = product.rating || { count: 0, total: 0, average: 0 };
      
      if (currentRating.count > 1) {
        const newCount = currentRating.count - 1;
        const newTotal = currentRating.total - review.rating;
        const newAverage = (newTotal / newCount).toFixed(1);
        
        await updateDoc(productRef, {
          rating: {
            count: newCount,
            total: newTotal,
            average: parseFloat(newAverage)
          }
        });
      } else {
        // If this was the only review, reset rating
        await updateDoc(productRef, {
          rating: {
            count: 0,
            total: 0,
            average: 0
          }
        });
      }
    }
    
    return true;
  } catch (error) {
    throw error;
  }
};

// Mark a review as helpful
export const markReviewHelpful = async (reviewId, userId) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    const reviewDoc = await getDoc(reviewRef);
    
    if (!reviewDoc.exists()) {
      throw new Error('Review not found');
    }
    
    const review = reviewDoc.data();
    const helpfulUsers = review.helpfulUsers || [];
    
    // Check if user has already marked this review as helpful
    if (helpfulUsers.includes(userId)) {
      // Remove user from helpfulUsers array
      const updatedHelpfulUsers = helpfulUsers.filter(id => id !== userId);
      
      await updateDoc(reviewRef, {
        helpful: increment(-1),
        helpfulUsers: updatedHelpfulUsers
      });
    } else {
      // Add user to helpfulUsers array
      await updateDoc(reviewRef, {
        helpful: increment(1),
        helpfulUsers: [...helpfulUsers, userId]
      });
    }
    
    // Get updated review
    const updatedReviewDoc = await getDoc(reviewRef);
    
    return {
      id: reviewId,
      ...updatedReviewDoc.data()
    };
  } catch (error) {
    throw error;
  }
};

// Report a review
export const reportReview = async (reviewId, reason) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    
    await updateDoc(reviewRef, {
      reported: true,
      reportCount: increment(1)
    });
    
    // Add report to reports collection
    await addDoc(collection(db, 'reports'), {
      reviewId,
      reason,
      timestamp: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    throw error;
  }
};

// Clear review reports
export const clearReviewReports = async (reviewId) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    
    await updateDoc(reviewRef, {
      reported: false,
      reportCount: 0
    });
    
    // Delete reports from reports collection
    const reportsQuery = query(
      collection(db, 'reports'),
      where('reviewId', '==', reviewId)
    );
    
    const reportsSnapshot = await getDocs(reportsQuery);
    
    reportsSnapshot.forEach(async (reportDoc) => {
      await deleteDoc(reportDoc.ref);
    });
    
    return true;
  } catch (error) {
    throw error;
  }
};
